/**
 * KONFIGURACJA AVATARÓW GRACZA.
 *
 * Phase 1 (MVP): 2 skiny — chłopiec + dziewczynka, oba unlocked od startu.
 *
 * Phase 2+ (dorzucanie): dopisuj tu kolejne wpisy + generuj PNG przez prompty
 * w `assets/avatars/prompts/`. Schema AvatarSkinDef już wspiera filtrowanie
 * po gender/unlockSource w UI selectora.
 *
 * Domyślny skin nowego gracza ustawiany w STARTER_SEED → balance.config.ts
 * (na razie hard-coded boy, w Step 2 dodamy modal wyboru przy pierwszym
 * uruchomieniu).
 */

import type { AvatarSkinDef, AvatarSkinId } from '@/domain/types/avatar';
import type { LocalizedString } from '@/domain/types/common';

// ─────────────────────────────────────────────────────────────────────────────
// „PEŁNY ALBUM: <biom>" — nagrody za komplet naklejek biomu w albumie Banku
// (DESIGN_ALBUM_BIOME_SKINS.md). Każdy biom 1–9 daje PARĘ skinów (chłopiec +
// dziewczynka) — serwer (`server/api/src/achievements/config.ts`,
// `rewardSkinIds`) dopisuje obie do `profile.unlockedSkins` przy odbiorze
// nagrody w „Osiągnięciach", gracz wybiera wersję w „Wygląd".
//
// Grafiki: `assets/avatars/avatar_album_<theme>_<boy|girl>.png` → optimize-assets
// → `.webp`. Prompty: `assets/avatars/prompts/avatar_album_<theme>_<gender>.md`
// (generowane Z OBRAZKIEM-WZOREM `avatar_boy/girl_default.png` — ta sama twarz).
//
// ⭐ GO-LIVE biomu = `enabled: true` w jego wierszu, DOPIERO gdy obie grafiki
// leżą w assets/avatars/. Test `tests/albumBiomeSkins.test.mjs` pada, jeśli
// włączony skin nie ma pliku (zero niebieskich prostokątów zamiast postaci).
// ─────────────────────────────────────────────────────────────────────────────

interface AlbumSkinVariant {
  name: LocalizedString;
  description: LocalizedString;
}

interface AlbumSkinPairSpec {
  biomeId: string;
  /** Część ID: `avatar_album_<theme>_<boy|girl>` (mirror serwerowego `skinTheme`). */
  theme: string;
  /** Staging: `false` dopóki grafiki nie są wgrane (patrz nagłówek). */
  enabled: boolean;
  boy: AlbumSkinVariant;
  girl: AlbumSkinVariant;
}

const ALBUM_SKIN_PAIRS: readonly AlbumSkinPairSpec[] = [
  {
    biomeId: 'biome-meadow',
    theme: 'carrot',
    enabled: true,
    boy: {
      name: { pl: 'Marchewkowy Chłopiec', en: 'Carrot Boy' },
      description: {
        pl: 'Kostium wielkiej marchewki z zieloną natką — widać tylko buzię.',
        en: 'Big carrot costume with a green leafy top — only the face peeks out.',
      },
    },
    girl: {
      name: { pl: 'Marchewkowa Dziewczynka', en: 'Carrot Girl' },
      description: {
        pl: 'Kostium wielkiej marchewki z zieloną natką — widać buzię i kucyki.',
        en: 'Big carrot costume with a green leafy top — the face and pigtails peek out.',
      },
    },
  },
  {
    biomeId: 'biome-mountain',
    theme: 'backpack',
    enabled: true,
    boy: {
      name: { pl: 'Mały Turysta', en: 'Little Hiker' },
      description: {
        pl: 'Klasyczny strój i wielki plecak turystyczny z karimatą.',
        en: 'Classic outfit with a big hiking backpack and a sleeping mat.',
      },
    },
    girl: {
      name: { pl: 'Mała Turystka', en: 'Little Hiker' },
      description: {
        pl: 'Klasyczny strój i wielki plecak turystyczny z karimatą.',
        en: 'Classic outfit with a big hiking backpack and a sleeping mat.',
      },
    },
  },
  {
    biomeId: 'biome-starwars',
    theme: 'farmer',
    enabled: true,
    boy: {
      name: { pl: 'Farmer', en: 'Farmer Boy' },
      description: {
        pl: 'Słomkowy kapelusz, koszula w kratę, ogrodniczki i kłos pszenicy w buzi.',
        en: 'Straw hat, plaid shirt, overalls and a wheat stalk in the mouth.',
      },
    },
    girl: {
      name: { pl: 'Farmerka', en: 'Farmer Girl' },
      description: {
        pl: 'Słomkowy kapelusz, koszula w kratę, sukienka-ogrodniczka i kłos pszenicy w buzi.',
        en: 'Straw hat, plaid shirt, overall dress and a wheat stalk in the mouth.',
      },
    },
  },
  {
    biomeId: 'biome-pixel',
    theme: 'pixel',
    enabled: true,
    boy: {
      name: { pl: 'Pikselowy Król', en: 'Pixel King' },
      description: {
        pl: 'Cały z pikseli, ze złotą pikselową koroną.',
        en: 'Made entirely of pixels, with a golden pixel crown.',
      },
    },
    girl: {
      name: { pl: 'Pikselowa Królowa', en: 'Pixel Queen' },
      description: {
        pl: 'Cała z pikseli, ze złotą pikselową koroną.',
        en: 'Made entirely of pixels, with a golden pixel crown.',
      },
    },
  },
  {
    biomeId: 'biome-ocean',
    theme: 'fish',
    enabled: true,
    boy: {
      name: { pl: 'Chłopiec Rybka', en: 'Fish Boy' },
      description: {
        pl: 'Kostium ryby w paski ze śmiesznymi płetwami — widać tylko buzię.',
        en: 'Striped fish costume with funny fins — only the face peeks out.',
      },
    },
    girl: {
      name: { pl: 'Dziewczynka Rybka', en: 'Fish Girl' },
      description: {
        pl: 'Kostium ryby w paski ze śmiesznymi płetwami — widać buzię i kucyki.',
        en: 'Striped fish costume with funny fins — the face and pigtails peek out.',
      },
    },
  },
  {
    biomeId: 'biome-food',
    theme: 'burger',
    enabled: true,
    boy: {
      name: { pl: 'Burgerowy Chłopiec', en: 'Burger Boy' },
      description: {
        pl: 'Kostium burgera: bułka z sezamem, sałata, ser i kotlet — widać tylko buzię.',
        en: 'Burger costume: sesame bun, lettuce, cheese and patty — only the face peeks out.',
      },
    },
    girl: {
      name: { pl: 'Burgerowa Dziewczynka', en: 'Burger Girl' },
      description: {
        pl: 'Kostium burgera: bułka z sezamem, sałata, ser i kotlet — widać buzię i kucyki.',
        en: 'Burger costume: sesame bun, lettuce, cheese and patty — the face and pigtails peek out.',
      },
    },
  },
  {
    biomeId: 'biome-atlantis',
    theme: 'trident',
    enabled: true,
    boy: {
      name: { pl: 'Władca Atlantydy', en: 'Lord of Atlantis' },
      description: {
        pl: 'Złoty trójząb w ręce i aura błyskawic.',
        en: 'Golden trident in hand and a lightning aura.',
      },
    },
    girl: {
      name: { pl: 'Władczyni Atlantydy', en: 'Lady of Atlantis' },
      description: {
        pl: 'Złoty trójząb w ręce i aura błyskawic.',
        en: 'Golden trident in hand and a lightning aura.',
      },
    },
  },
  {
    biomeId: 'biome-school',
    // Historyczne ID nagrody zachowujemy dla już odblokowanych skinów graczy.
    theme: 'toilet',
    enabled: true,
    boy: {
      name: { pl: 'Uczeń', en: 'Schoolboy' },
      description: {
        pl: 'Granatowy mundurek szkolny: marynarka, biała koszula, czerwony krawat i spodenki.',
        en: 'Navy school uniform with a blazer, white shirt, red tie and shorts.',
      },
    },
    girl: {
      name: { pl: 'Uczennica', en: 'Schoolgirl' },
      description: {
        pl: 'Granatowy mundurek szkolny: marynarka, biała koszula, czerwony krawat i plisowana spódniczka.',
        en: 'Navy school uniform with a blazer, white shirt, red tie and pleated skirt.',
      },
    },
  },
  {
    biomeId: 'biome-arctic',
    theme: 'penguin',
    enabled: true,
    boy: {
      name: { pl: 'Pingwinek', en: 'Penguin Boy' },
      description: {
        pl: 'Puchaty kostium pingwina — widać tylko buzię.',
        en: 'Fluffy penguin costume — only the face peeks out.',
      },
    },
    girl: {
      name: { pl: 'Pingwinka', en: 'Penguin Girl' },
      description: {
        pl: 'Puchaty kostium pingwina — widać buzię i kucyki.',
        en: 'Fluffy penguin costume — the face and pigtails peek out.',
      },
    },
  },
];

const ALBUM_SKIN_GENDERS: readonly ('boy' | 'girl')[] = ['boy', 'girl'];

/** Para specyfikacji → dwa pełne `AvatarSkinDef` (chłopiec, dziewczynka). */
function albumSkinDefs(spec: AlbumSkinPairSpec): AvatarSkinDef[] {
  return ALBUM_SKIN_GENDERS.map((gender) => {
    const id = `avatar_album_${spec.theme}_${gender}` as AvatarSkinId;
    return {
      id,
      name: spec[gender].name,
      gender,
      description: spec[gender].description,
      unlockedByDefault: false,
      unlockSource: 'achievement',
      albumBiomeId: spec.biomeId,
      enabled: spec.enabled,
      art: { textureKey: id },
      tags: ['album', spec.theme],
    };
  });
}

export const AVATAR_SKINS: AvatarSkinDef[] = [
  {
    id: 'avatar_boy_default',
    name: { pl: 'Chłopiec', en: 'Boy' },
    gender: 'boy',
    description: {
      pl: 'Klasyczny strój: czerwona koszulka, niebieskie spodenki, brązowe włosy.',
      en: 'Classic outfit: red shirt, blue shorts, brown hair.',
    },
    unlockedByDefault: true,
    unlockSource: 'default',
    art: {
      textureKey: 'avatar_boy_default',
      // Anchors zarezerwowane na przyszłość — Phase 1 renderer ich nie używa.
      anchors: {
        head:         { x: 0, y: -52 },
        bodyTop:      { x: 0, y: -20 },
        accessoryTop: { x: 0, y: -68 },
      },
    },
    tags: ['starter', 'classic'],
  },
  {
    id: 'avatar_girl_default',
    name: { pl: 'Dziewczynka', en: 'Girl' },
    gender: 'girl',
    description: {
      pl: 'Klasyczny strój: fioletowa koszulka, różowa spódniczka, kucyki.',
      en: 'Classic outfit: purple shirt, pink skirt, pigtails.',
    },
    unlockedByDefault: true,
    unlockSource: 'default',
    art: {
      textureKey: 'avatar_girl_default',
      anchors: {
        head:         { x: 0, y: -52 },
        bodyTop:      { x: 0, y: -20 },
        accessoryTop: { x: 0, y: -68 },
      },
    },
    tags: ['starter', 'classic'],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // EVENT „Wakacje Capibar" (EV-3) — nagroda za komplet Albumu Wakacyjnego (10/10).
  // unlockedByDefault:false → odblokowywane serwerowo przy claimie albumu
  // (dodanie id do profile.unlockedSkins). Po evencie skin ZOSTAJE (pamiątka).
  // Grafiki: assets/avatars/avatar_event_capibara_summer_boy/_girl.(png|webp).
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'avatar_event_capibara_summer_boy',
    name: { pl: 'Letni Chłopiec', en: 'Summer Boy' },
    gender: 'boy',
    description: {
      pl: 'Wakacyjny strój: słomkowy kapelusz, letnia koszulka, kąpielówki, klapki.',
      en: 'Holiday outfit: straw hat, summer shirt, swim shorts, flip-flops.',
    },
    unlockedByDefault: false,
    unlockSource: 'event',
    eventId: 'event-capibara-summer',
    art: { textureKey: 'avatar_event_capibara_summer_boy' },
    tags: ['event', 'summer'],
  },
  {
    id: 'avatar_event_capibara_summer_girl',
    name: { pl: 'Letnia Dziewczynka', en: 'Summer Girl' },
    gender: 'girl',
    description: {
      pl: 'Wakacyjny strój: słomkowy kapelusz, kostium kąpielowy, klapki.',
      en: 'Holiday outfit: straw hat, swimsuit, flip-flops.',
    },
    unlockedByDefault: false,
    unlockSource: 'event',
    eventId: 'event-capibara-summer',
    art: { textureKey: 'avatar_event_capibara_summer_girl' },
    tags: ['event', 'summer'],
  },

  // „Pełny album: <biom>" — 9 par (18 skinów), patrz ALBUM_SKIN_PAIRS wyżej.
  ...(['boy', 'girl'] as const).map((gender): AvatarSkinDef => ({
    id: `avatar_event_capibara_halloween_${gender}`,
    name: { pl: gender === 'boy' ? 'Dyniowy Chłopiec' : 'Dyniowa Dziewczynka', en: gender === 'boy' ? 'Pumpkin Boy' : 'Pumpkin Girl' },
    gender,
    description: { pl: 'Przytulny dyniowy kostium z listkiem.', en: 'A cozy pumpkin costume with a leaf.' },
    unlockedByDefault: false,
    unlockSource: 'event',
    eventId: 'event-capibara-halloween',
    art: { textureKey: `avatar_event_capibara_halloween_${gender}` },
    tags: ['event', 'halloween'],
  })),
  ...ALBUM_SKIN_PAIRS.flatMap(albumSkinDefs),
];

/** Mapa pomocnicza id → AvatarSkinDef. */
export const AVATAR_SKINS_BY_ID: Record<string, AvatarSkinDef> = Object.fromEntries(
  AVATAR_SKINS.map((s) => [s.id, s]),
);

/** Skin gotowy do użycia (nie czeka w stagingu na grafikę). */
export function isAvatarSkinEnabled(skin: AvatarSkinDef): boolean {
  return skin.enabled !== false;
}

/**
 * Włączone skiny-nagrody za „Pełny album" danego biomu (chłopiec, dziewczynka).
 * Pusta lista = biom bez nagrody albo nagroda jeszcze w stagingu.
 */
export function albumRewardSkins(biomeId: string): AvatarSkinDef[] {
  return AVATAR_SKINS.filter((s) => s.albumBiomeId === biomeId && isAvatarSkinEnabled(s));
}

/**
 * Wersja skina-nagrody pasująca do płci obecnie noszonego skina (chłopiec
 * w stroju chłopca → wersja chłopca). Nieznany/neutralny noszony skin → chłopiec.
 */
export function pickSkinForWearer(
  candidates: readonly AvatarSkinDef[],
  wornSkinId: string,
): AvatarSkinDef | undefined {
  const wornGender = AVATAR_SKINS_BY_ID[wornSkinId]?.gender;
  return (
    candidates.find((s) => s.gender === wornGender) ??
    candidates.find((s) => s.gender === 'boy') ??
    candidates[0]
  );
}

/** Skin domyślny dla nowego gracza (przed wyborem w selectorze). */
export const DEFAULT_STARTER_SKIN_ID = 'avatar_boy_default';

/** Skiny dostępne od startu (do UI selectora pierwszego uruchomienia). */
export const STARTER_AVAILABLE_SKINS: AvatarSkinDef[] =
  AVATAR_SKINS.filter((s) => s.unlockedByDefault);

/**
 * ID skinów odblokowanych domyślnie (boy/girl). Świeży save seeduje nimi
 * `profile.unlockedSkins`; serwer ma własny mirror (`serialize.ts`) — domyślne
 * skiny są ZAWSZE „noszalne", niezależnie od listy w save.
 */
export const DEFAULT_UNLOCKED_SKIN_IDS: AvatarSkinId[] =
  STARTER_AVAILABLE_SKINS.map((s) => s.id);
